<script lang="ts">
  import { untrack } from 'svelte';
  import { store } from './store.svelte.ts';
  import { describeScan, type ScanMessage } from './scan/outcome.ts';
  import { chime, haptic, primeAudio } from './feedback.ts';
  import { normaliseScanInput } from '@chariot/contract';
  import Scanner from './Scanner.svelte';

  /**
   * The cart found in a corner once the sweep is closed.
   *
   * Without this the only way to correct one cart is to sweep the whole site again — which
   * means it does not get corrected. The scan is recorded exactly like any other; what is
   * deliberately *not* recorded is any conclusion about the carts nobody looked at.
   */

  interface Props {
    /** Fixed when we already know where we are; otherwise the operator picks. */
    site_code?: string | null;
    /** Pre-filled when starting from a named cart, e.g. a round's missing list. */
    cart_code?: string | null;
    onclose: () => void;
  }
  let { site_code = null, cart_code = null, onclose }: Props = $props();

  // Seeds, not mirrors: the sheet is mounted fresh per use, and from here on the operator
  // owns both fields.
  let chosenSite = $state(untrack(() => site_code) ?? '');
  let manualCart = $state(untrack(() => cart_code) ?? '');
  let scannerOpen = $state(false);
  let feedback = $state<ScanMessage | null>(null);
  let busy = $state(false);

  const site = $derived(store.siteByCode.get(chosenSite) ?? null);
  const activeSites = $derived(store.sites.filter((s) => s.status === 'active'));
  const recorded = $derived(store.correction?.scanned ?? []);
  const started = $derived(store.correction?.site_code === chosenSite && chosenSite !== '');

  const say = (o: Parameters<typeof describeScan>[0]) => describeScan(o, (c) => store.siteName(c));

  function announce(msg: ScanMessage) {
    haptic(msg.tone);
    if (store.scanSound) chime(msg.tone);
    feedback = msg;
    return msg;
  }

  /** The round is opened on first use, not on mount: an operator who backs out writes nothing. */
  async function ensureStarted(): Promise<boolean> {
    if (started) return true;
    if (!chosenSite) return false;
    const outcome = await store.beginCorrection(chosenSite);
    if (outcome.kind !== 'round_opened') {
      feedback = say(outcome);
      return false;
    }
    return true;
  }

  async function openScanner() {
    primeAudio();
    if (!(await ensureStarted())) return;
    feedback = null;
    scannerOpen = true;
  }

  /** From the camera. The scanner holds and announces the result itself. */
  async function onScan(text: string) {
    const msg = say(await store.handleScan(text));
    feedback = msg;
    return msg;
  }

  async function submitManual() {
    primeAudio();
    if (!manualCart.trim() || !(await ensureStarted())) return;
    busy = true;
    try {
      announce(say(await store.handleScan(normaliseScanInput(manualCart))));
      manualCart = '';
    } finally {
      busy = false;
    }
  }

  /**
   * Any way out ends the correction — the button, or the screen being left. An opening left
   * dangling concludes nothing, but it would surface in the next correction as a cart the
   * operator did not just find.
   */
  $effect(() => () => void store.endCorrection());

  function finish() {
    onclose();
  }

  const label = (code: string) => store.cartByCode.get(code)?.name ?? code;
</script>

<div class="overlay" role="dialog" aria-modal="true" aria-label="Enregistrer un chariot retrouvé">
  <div class="sheet">
    <h2>Chariot retrouvé</h2>

    {#if site_code}
      <p class="small muted">
        À {site?.name ?? site_code}, sans refaire la tournée. Rien d'autre n'est déclaré manquant.
      </p>
    {:else}
      <label class="field">
        <span>Où l'avez-vous trouvé ?</span>
        <select bind:value={chosenSite} disabled={started}>
          <option value="">— choisir un site —</option>
          {#each activeSites as s (s.code)}<option value={s.code}>{s.name}</option>{/each}
        </select>
      </label>
    {/if}

    <button class="primary block big" onclick={openScanner} disabled={!chosenSite}>
      Scanner le chariot
    </button>

    <form class="manual" onsubmit={(e) => { e.preventDefault(); void submitManual(); }}>
      <input
        bind:value={manualCart}
        placeholder="CHT-000123"
        aria-label="Saisir un code de chariot"
        autocapitalize="characters"
        autocomplete="off"
        spellcheck="false"
      />
      <button type="submit" disabled={!chosenSite || !manualCart.trim() || busy}>Ajouter</button>
    </form>

    {#if feedback}
      <div class="banner {feedback.tone === 'neutral' ? 'info' : feedback.tone}" style="margin-top:.7rem">
        {feedback.text}
      </div>
    {/if}

    {#if recorded.length > 0}
      <ul class="list">
        {#each recorded as code (code)}
          <li>
            <span class="grow truncate row-title">{label(code)}</span>
            <span class="mono tiny subtle">{code}</span>
          </li>
        {/each}
      </ul>
    {/if}

    <div class="row" style="margin-top:.9rem">
      <button class={recorded.length > 0 ? 'primary grow' : 'ghost grow'} onclick={finish}>
        {recorded.length > 0 ? 'Terminé' : 'Fermer'}
      </button>
    </div>
  </div>
</div>

{#if scannerOpen}
  <Scanner
    title={site?.name ?? 'Chariot retrouvé'}
    hint="Visez le QR du chariot retrouvé"
    forceFallback={store.forceFallbackScanner}
    holdMs={store.scanHoldMs}
    sound={store.scanSound}
    status={recorded.length > 0 ? `${recorded.length} enregistré${recorded.length === 1 ? '' : 's'}` : ''}
    onresult={onScan}
    onclose={() => (scannerOpen = false)}
  />
{/if}

<style>
  .manual { display: flex; gap: .5rem; margin-top: .6rem; }
  .manual input { font-family: var(--mono); }
  .manual button { flex: none; }
</style>
